import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";

import { User } from "src/app/models/user";
import { FormValidationService } from "src/app/services/form-validation.service";
import { COUNTRIES } from "src/constants/validation-patterns";
import { FormBaseComponent } from "../form-base.component";

enum FIELDS {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  PHONE = 'phone',
  EMAIL = 'email',
  EMAIL_CONFIRM = 'confirmEmail',
  STREET = 'street',
  ADDITIONAL_BILLING_ADDRESS_LINE = 'additionalBillingAddressLine',
  CITY = 'city',
  ZIP_POSTAL_CODE = 'zipPostalCode',
  COUNTRY = 'country',
  STATE = 'state'
}

@Component({
  selector: 'account-form',
  templateUrl: './account-form.html',
  styleUrls: ['./account-form.scss']
})

export class AccountFormComponent extends FormBaseComponent {

  @ViewChild('accountForm') accountForm: NgForm;

  @Input() guest: User = {} as User;
  @Input() countriesList: Array<any> = [];
  @Input() statesList: Array<any> = [];
  @Input() submittedAccountForm: boolean = false;
  @Input() emailToConfirm: string = '';
  @Input() euSpecified : boolean = false;
  @Input() braintreeDefaultZipPostalCode: string = '';
  @Input() braintreeDefaultCountry: string = 'United States';
  @Input() braintreeDefaultState: string = '';

  @Output() linkForm = new EventEmitter();
  @Output() validateAddressAndCalculateTaxes = new EventEmitter<string>();
  @Output() rememberAccountInformation = new EventEmitter<void>();
  @Output() readyToCreateLead = new EventEmitter<void>();
  @Output() emitConfirmEmail = new EventEmitter<string>();
  @Output() countryChange = new EventEmitter();
  @Output() stateChange = new EventEmitter();

  readonly FIELDS = FIELDS;
  
  // Private alias for cleaner code - initialized in constructor
  private vs!: FormValidationService;

  // Expose validation patterns for template use
  readonly errorImage: string = this.formValidationService.errorImage;
  emailPattern: string = this.formValidationService.emailPattern;
  textPattern: string = this.formValidationService.textPattern;
  mixedPattern: string = this.formValidationService.mixedPattern;
  usaZipPattern: string = this.formValidationService.usaZipPattern;
  canadianPostalPattern: string = this.formValidationService.canadianPostalPattern;
  numberPattern: string = this.formValidationService.numberPattern;
  phonePattern: string = this.formValidationService.phonePattern;
  addressPattern: string = this.formValidationService.addressPattern;

  isInvalidState: boolean = false;
  firstValidation: { value: boolean } = { value: true };

  constructor(private formValidationService: FormValidationService) {
    super();
    // Initialize alias for cleaner code
    this.vs = this.formValidationService;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setupFormValidators();
      this.linkForm.emit(this.accountForm);
    }, 0);
  }

  /**
   * Sets up validators for all form fields
   * Centralizes validator configuration to reduce duplication
   */
  private setupFormValidators(): void {
    if (!this.accountForm?.controls) {
      return;
    }

    const country = this.guest?.country || this.braintreeDefaultCountry || COUNTRIES.UNITED_STATES;

    // FIRST_NAME and LAST_NAME - same validators
    this.vs.setFieldValidators(
      this.accountForm,
      String(this.FIELDS.FIRST_NAME),
      this.vs.getNameValidators()
    );
    this.vs.setFieldValidators(
      this.accountForm,
      String(this.FIELDS.LAST_NAME),
      this.vs.getNameValidators()
    );

    // PHONE - optional field with pattern validation
    this.vs.setFieldValidators(
      this.accountForm,
      String(this.FIELDS.PHONE),
      this.vs.getPhoneValidators()
    );

    // EMAIL and EMAIL_CONFIRM - same validators
    const emailValidators = this.vs.getEmailValidators(
      this,
      String(this.FIELDS.EMAIL),
      String(this.FIELDS.EMAIL_CONFIRM),
      this.firstValidation
    );
    this.vs.setFieldValidators(this.accountForm, String(this.FIELDS.EMAIL), emailValidators);
    this.vs.setFieldValidators(this.accountForm, String(this.FIELDS.EMAIL_CONFIRM), emailValidators);

    // STREET - required with address pattern
    this.vs.setFieldValidators(
      this.accountForm,
      String(this.FIELDS.STREET),
      this.vs.getAddressValidators()
    );

    // CITY - required with text pattern
    this.vs.setFieldValidators(
      this.accountForm,
      String(this.FIELDS.CITY),
      this.vs.getCityValidators()
    );

    // ZIP_POSTAL_CODE - uses custom validator based on country
    this.vs.setFieldValidators(
      this.accountForm,
      String(this.FIELDS.ZIP_POSTAL_CODE),
      this.vs.getPostalCodeValidators(country, this.braintreeDefaultCountry)
    );
  }

  /**
   * Gets the localized label for a form field
   * @param label - The label key to retrieve
   * @returns The localized label string
   */
  getLabel(label: string): string {
    return this.vs.getCheckoutLabel(label, this.braintreeDefaultCountry);
  }

  /**
   * Returns the appropriate pattern for postal/zip code based on country
   * Used in HTML template for client-side validation
   */
  getPostalCodePattern(): string {
    const country = this.guest?.country || this.braintreeDefaultCountry || COUNTRIES.UNITED_STATES;
    return this.vs.getPostalCodePattern(country, this.braintreeDefaultCountry);
  }

  validate_AddressAndCalculateTaxes(address: string) {
    // Revalidate postal code when address/country changes
    const country = this.guest?.country || this.braintreeDefaultCountry || COUNTRIES.UNITED_STATES;
    this.vs.revalidatePostalCode(
      this.accountForm,
      String(this.FIELDS.ZIP_POSTAL_CODE),
      country,
      this.braintreeDefaultCountry
    );
    this.validateAddressAndCalculateTaxes.emit(address);
  }

  /**
   * Handles blur event on postal code field to trigger validation
   */
  onPostalCodeBlur(): void {
    this.vs.onPostalCodeBlur(this.accountForm, String(this.FIELDS.ZIP_POSTAL_CODE));
  }

  remember_AccountInformation() {
    this.rememberAccountInformation.emit();
  }

  ready_ToCreateLead() {
    this.readyToCreateLead.emit();
  }

  emitConfirmEmailToCheckout() {
    this.emitConfirmEmail.emit(this.emailToConfirm);
  }

  validateEmailFields() {
    this.vs.validateEmailFields(
      this.accountForm,
      String(this.FIELDS.EMAIL),
      String(this.FIELDS.EMAIL_CONFIRM)
    );
  }
  
  /**
   * Checks if the app is running in Facebook's in-app browser
   * @returns true if running in Facebook app, false otherwise
   */
  isFacebookApp(): boolean {
    return this.vs.isFacebookApp();
  }

  // Display the state field if it is not included in the hideStateByCountryList
  displayStateField() {
    return this.vs.displayStateField(this.guest.country);
  }

  onCountryChange(): void {
    this.countryChange.emit(true);
  }

  onStateChange(): void {
    this.stateChange.emit(true);
  }
  facebookAppStateChangeHandler() {
    console.log('facebookAppStateChangeHandler');
    try {
      let fbcountry:any = (document.querySelector('#FBCountryCode') as any).value;

      if(!fbcountry || fbcountry == '') fbcountry = 'US';
      let FBStates = ((window as any)['countries']).find(  ({ value }:{[key:string]:any}) => value === fbcountry);
      if(!FBStates) return;
      let FBState = FBStates.states;
      (document.querySelector('#facebookstate') as any).value = FBState[(document.querySelector('#FBStateCode') as any).value];

      setTimeout(() => {
        if (this.accountForm.form.controls.additionalBillingAddressLine.value == this.accountForm.form.controls.street.value || this.accountForm.form.controls.additionalBillingAddressLine.value == '') {
          if (document.querySelector('#FBAddressLine2') && (document.querySelector('#FBAddressLine2') as any).value != '') {
            this.accountForm.form.controls.additionalBillingAddressLine.patchValue((document.querySelector('#FBAddressLine2') as any).value);
            this.accountForm.form.controls.additionalBillingAddressLine.markAsDirty();
            this.accountForm.form.controls.additionalBillingAddressLine.updateValueAndValidity();
          }
        } 
      }, 300);

      if(this.accountForm.form.controls.street.value == this.accountForm.form.controls.additionalBillingAddressLine.value) {
        this.accountForm.form.controls.additionalBillingAddressLine.patchValue('');
        this.accountForm.form.controls.additionalBillingAddressLine.markAsDirty();
        this.accountForm.form.controls.additionalBillingAddressLine.updateValueAndValidity();
      }
      if (FBStates.label != '' && FBStates.label != this.accountForm.form.controls.country.value) {
        this.accountForm.form.controls.country.patchValue(FBStates.label);
        this.accountForm.form.controls.country.markAsDirty();
        this.accountForm.form.controls.country.updateValueAndValidity();
        this.accountForm.form.markAsDirty();
       // this.confirmAddress();
       this.validate_AddressAndCalculateTaxes('billing');
       this.onCountryChange();
      }
      setTimeout(() => {
        if (FBState[(document.querySelector('#FBStateCode') as any).value] != '') {
          this.accountForm.form.controls.state.patchValue(FBState[(document.querySelector('#FBStateCode') as any).value]);
          this.accountForm.form.controls.state.markAsDirty();
          this.accountForm.form.controls.state.updateValueAndValidity();

        }
        this.accountForm.form.markAsDirty();

      
      //  this.confirmAddress();
        this.validate_AddressAndCalculateTaxes('billing');
        this.onStateChange();

      },300)
    } catch (e) {
      console.log('Facebook app state changed', e);
    }
  }

}
